// Hoe dicht een thema bij publicatie staat — de sortering van de themalijst.
//
// Vier treden, en alleen de EIGEN segmentcellen tellen mee: een effectbrede cel
// of een TOC-keuze kan nooit ónder dit thema gepubliceerd worden (onderwerpmodel,
// besluit 2), dus die maakt het thema ook niet publiceerbaar. Zou hij meetellen,
// dan stond een thema bovenaan als 'publiceerbaar' zonder dat er iets onder te
// publiceren valt.
//
// AI-CONTEXT: See docs/features/intelligence-thema-eerst.md

import { splitAgendaRowsForTheme } from './filterThemeRows'
import { BENCHMARK_STATE } from './effectBenchmarkGate'

/** Hoger = dichter bij buiten. */
export const STAGES = Object.freeze({
    published: 3,
    publishable: 2,
    inProgress: 1,
    none: 0,
})

const LABELS = Object.freeze({
    [STAGES.published]: 'Gepubliceerd',
    [STAGES.publishable]: 'Publiceerbaar',
    [STAGES.inProgress]: 'In aanloop',
    [STAGES.none]: 'Nog niets',
})

/**
 * @typedef {object} ThemeReadiness
 * @property {number} stage - een van STAGES
 * @property {string} label - Nederlands label van die trede
 * @property {number} readiness - 0–1, de hoogste gereedheid van de eigen cellen
 * @property {boolean} needsReview - een eigen publicatie is door de nachtelijke controle gemarkeerd
 */

/**
 * @param {object} args
 * @param {{id: string, msiId: string, targetgroup: string, publishedCount?: number}} args.theme
 * @param {Array<object>|null} args.agendaRows - alle agendarijen van de pagina
 * @param {Set<string>|null} args.publishedCellKeys - cellen die al buiten staan; null = nog niet bekend
 * @param {Array<object>|null} args.publishedInsights - gepubliceerde inzichten
 * @param {string} [args.benchmarkState] - de stand van de benchmark van het effect (BENCHMARK_STATE); weglaten = onbekend, en dan niet publiceerbaar
 * @returns {ThemeReadiness}
 */
export const themeReadiness = ({
    theme, agendaRows, publishedCellKeys, publishedInsights,
    benchmarkState = BENCHMARK_STATE.unknown,
}) => {
    const { own } = splitAgendaRowsForTheme(theme, agendaRows)
    const eigenPublicaties = (Array.isArray(publishedInsights) ? publishedInsights : [])
        .filter((i) => i && i.ThemeID === theme.id)
    const needsReview = eigenPublicaties.some((i) => i.Drift && i.Drift.needsReview === true)

    const readiness = own.reduce((max, row) => (
        typeof row.readiness === 'number' && row.readiness > max ? row.readiness : max
    ), 0)

    const gepubliceerd = eigenPublicaties.length > 0 || (theme.publishedCount || 0) > 0
    // Zolang niet bekend is wat er buiten staat, is 'publiceerbaar' een gok
    // de verkeerde kant op: je zou het zeggen over iets dat al buiten staat.
    // Twee poorten, en allebei moeten ze open staan. P2 gaat over de CEL (dit
    // patroon bij deze doelgroep); de benchmark gaat over het EFFECT waar die
    // cel een plak van is. Haalt de benchmark de klantdrempel niet, dan weigert
    // de server de publicatie — en dan mag de trede hem niet beloven
    // (ADR-040). Onbekend telt als dicht: bij twijfel niet publiceerbaar.
    // AI-CONTEXT: See ./effectBenchmarkGate.js
    const publiceerbaar = publishedCellKeys instanceof Set
        && benchmarkState === BENCHMARK_STATE.ok
        && own.some((row) => row.publishable === true && !publishedCellKeys.has(row.cellId))

    let stage = STAGES.none
    if (gepubliceerd) stage = STAGES.published
    else if (publiceerbaar) stage = STAGES.publishable
    else if (own.length > 0) stage = STAGES.inProgress

    return { stage, label: LABELS[stage], readiness, needsReview }
}

/**
 * Sorteervergelijking voor `{theme, stage, readiness}`: trede omlaag, dan
 * gereedheid omlaag, dan de sleutel — de invoervolgorde komt van Firestore en
 * wisselt tussen twee snapshots.
 * AI-CONTEXT: See docs/pitfalls.md#database-coarse-sort-key-ties-break-arbitrarily
 *
 * @param {{theme: {id: string}, stage: number, readiness: number}} a
 * @param {{theme: {id: string}, stage: number, readiness: number}} b
 * @returns {number}
 */
export const compareByReadiness = (a, b) => (
    (b.stage - a.stage)
    || (b.readiness - a.readiness)
    || String(a.theme.id).localeCompare(String(b.theme.id))
)

export default themeReadiness
